/**
 * useProjectPageTracker Hook
 *
 * Tracks the last visited page within a project context.
 * Saves to localStorage for persistence across sessions.
 *
 * This hook should be called in the Layout component to track
 * navigation for all project pages.
 */

import { useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useAuth } from '@frontend/contexts/AuthContext';

/** localStorage key prefix matching ProjectDefaultRedirect */
const STORAGE_KEY_PREFIX = 'project-last-page-';

/**
 * Extract page segment from project URL.
 *
 * @param pathname - Current URL pathname
 * @param projectId - Current project ID
 * @returns Page segment or null if not a project sub-page
 *
 * @example
 * extractPageFromPath('/project/123/onboardings', '123') // 'onboardings'
 * extractPageFromPath('/project/123/stripe/products', '123') // 'stripe/products'
 * extractPageFromPath('/project/123', '123') // null (base project page)
 */
function extractPageFromPath(pathname: string, projectId: string): string | null {
  const prefix = `/project/${projectId}/`;

  if (!pathname.startsWith(prefix)) {
    return null;
  }

  const segment = pathname.slice(prefix.length);
  // Handle empty segment (base project URL) or segments with query params
  const cleanSegment = segment.split('?')[0];
  return cleanSegment || null;
}

/**
 * Hook that tracks last visited project page.
 *
 * Saves the current page to localStorage whenever navigation occurs
 * within a project context. Only tracks for non-admin users since
 * admins always see the Product page.
 *
 * @example
 * ```tsx
 * function Layout({ children }) {
 *   useProjectPageTracker();
 *   return <div>{children}</div>;
 * }
 * ```
 */
export function useProjectPageTracker(): void {
  const { projectId } = useParams<{ projectId: string }>();
  const location = useLocation();
  const { isAdmin } = useAuth();

  useEffect(() => {
    // Only track for non-admins (admins always see Product page)
    if (isAdmin || !projectId) {
      return;
    }

    const page = extractPageFromPath(location.pathname, projectId);

    // Don't save the base project URL (that's what we're redirecting FROM)
    if (page) {
      const storageKey = `${STORAGE_KEY_PREFIX}${projectId}`;
      localStorage.setItem(storageKey, page);
    }
  }, [location.pathname, projectId, isAdmin]);
}

export default useProjectPageTracker;
